import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CompassColor,
  Container,
  Input,
  Stack,
  VariantColor,
} from "@noom/wax-component-library";
import { useLocation } from "react-router-dom";
import { useLogger } from "@noom/hive-analytics";
import { usePasswordFlow, useAutoCookieFlow } from "@noom/hive-auth";

import { LOGIN_ERROR_MESSAGE_KEY } from "@modules/common/constants";
import { useLoginViewSignal } from "@modules/authentication/useLoginViewSignal";
import { BrandNeutralLoading } from "common/NoomLoader";
import { readInAppLoginCookie } from "hooks/useInAppLogin";

import { MixpanelTypes } from "../common/types";

// SecureRoute redirects unauthenticated users here with the originally
// requested location in `state.from`; `state.path` is the legacy shape kept for
// any callers that still pass it.
//
// IMPORTANT: hive-auth's `SecureRoute`/`Navigate` write history state via a raw
// `window.history.replaceState`, which React Router v6 does NOT surface through
// `useLocation().state` (it reads `history.state.usr`). So `state.from` is
// effectively always undefined here. The real post-login destination is instead
// persisted by hive-auth in the redirect cookie (`setRedirectPath(state.from)`
// inside `useNavigation`). We therefore return `undefined` when there's no
// router state and let the auth flows fall back to that cookie via
// `getRedirectPath()` — passing a hardcoded "/" here would override it and
// strip in-app deeplinks (e.g. /care → "/" → HomePage → /shipping-and-refills),
// because `useAutoCookieFlow` prefers the explicit arg over the saved path.
type LocationState = {
  from?: { pathname: string; search: string; hash: string };
  path?: string;
} | null;

const resolveRedirectPath = (state: LocationState): string | undefined => {
  if (state?.from) {
    return `${state.from.pathname}${state.from.search}${state.from.hash}`;
  }
  if (state?.path) {
    return state.path;
  }
  return undefined;
};

const readRelayedError = (): string | undefined => {
  const stored = sessionStorage.getItem(LOGIN_ERROR_MESSAGE_KEY);
  if (stored) {
    sessionStorage.removeItem(LOGIN_ERROR_MESSAGE_KEY);
    return stored;
  }
  return undefined;
};

// Component to serve a simple login form. hive-auth performs the CoachServer
// OAuth handshake (password + auto-cookie); patient validation and type-based
// routing happen after authentication via PatientProfileProvider / HomePage.
export const LoginPage: React.FC = () => {
  const { state } = useLocation();
  const mixpanelLogger = useLogger();
  const hasInAppLoginCookie = readInAppLoginCookie() !== null;

  const {
    login,
    error: passwordError,
    inProgress: isLoginInProgress,
  } = usePasswordFlow();
  const {
    isAvailable: isAutoLoginAvailable,
    inProgress: isAutoLoginInProgress,
    login: autoLogin,
    error: autoLoginError,
  } = useAutoCookieFlow();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    readRelayedError
  );

  const redirectPath = resolveRedirectPath(state as LocationState);

  // `passwordError` and `autoLoginError` are the SAME sticky error from
  // hive-auth's shared AuthContext (both flows read it), and a *successful*
  // login never clears it — so it can linger from an earlier failed attempt in
  // the same SPA session. Capture whatever is set at mount as a baseline and
  // treat only a *newer* error (a fresh object from an attempt made on this
  // page) as "live". This keeps a stale error from (a) surfacing a false "No
  // account found" / re-emitting `LoginFailed`, (b) clobbering a relayed
  // enrollment message, or (c) blocking the WebView auto-login below.
  const authError = passwordError ?? autoLoginError;
  const staleAuthErrorRef = useRef(authError);
  const liveAuthError =
    authError !== staleAuthErrorRef.current ? authError : undefined;
  const autoLoginAttemptedRef = useRef(false);

  // Attempt webview auto-login once the `autologin_data` cookie is available.
  // `isAutoLoginAvailable` can become true after hive-auth finishes
  // initializing, so this must react to dependency changes rather than only run
  // on mount. The ref prevents repeated attempts while the hook re-renders.
  useEffect(() => {
    if (
      isAutoLoginAvailable &&
      !isAutoLoginInProgress &&
      !liveAuthError &&
      !errorMessage &&
      !autoLoginAttemptedRef.current
    ) {
      autoLoginAttemptedRef.current = true;
      void autoLogin(redirectPath);
    }
  }, [
    autoLogin,
    isAutoLoginAvailable,
    isAutoLoginInProgress,
    errorMessage,
    liveAuthError,
    redirectPath,
  ]);

  // Surface auth failures (wrong credentials / failed auto-login).
  useEffect(() => {
    if (liveAuthError) {
      setErrorMessage(
        "No account found. Please check you entered your information correctly. Otherwise please contact Support@noom.com"
      );
      mixpanelLogger.log(MixpanelTypes.LoginFailed, {
        err: liveAuthError,
        errorMessage: liveAuthError.message ?? "no error message",
      });
    }
  }, [liveAuthError, mixpanelLogger]);

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isLoginInProgress) {
      return;
    }

    if (password.length === 0 || email.length === 0) {
      setErrorMessage("Must provide valid email and password");
      return;
    }

    setErrorMessage(undefined);
    login(email, password, undefined, redirectPath);
  };

  // While a webview auto-login is in flight, show a loader instead of the form.
  // If a relayed enrollment/auth message is present, suppress auto-login so the
  // user can actually see the error instead of being immediately re-authenticated.
  const isAwaitingAutoLogin =
    (hasInAppLoginCookie || isAutoLoginAvailable) &&
    !liveAuthError &&
    !errorMessage;

  // Classify the login screen only once it has settled into the form — a
  // WebView that auto-logs-in successfully never gets here.
  useLoginViewSignal(!isAwaitingAutoLogin);

  if (isAwaitingAutoLogin) {
    return <BrandNeutralLoading />;
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <Stack padding="20px;">
        <Container>
          <Input
            data-cy="login-email-input"
            placeholder="Email"
            type="email"
            value={email}
            onChange={(evt) => setEmail(evt.currentTarget.value)}
          />
        </Container>
        <Container>
          <Input
            data-cy="login-password-input"
            placeholder="Password"
            type="password"
            value={password}
            onChange={(evt) => setPassword(evt.currentTarget.value)}
          />
        </Container>
        <Container centerHorizontal>
          <Button
            variantColor={VariantColor.primary}
            color={CompassColor.white}
            data-testid="login-button"
            type="submit"
            disabled={isLoginInProgress}
          >
            Login
          </Button>
        </Container>
        {errorMessage && (
          <Container data-cy="login-error-message" color="red" centerHorizontal>
            {errorMessage}
          </Container>
        )}
      </Stack>
    </form>
  );
};
